import { sql } from 'drizzle-orm';
import { index, integer, real, sqliteTable, text } from 'drizzle-orm/sqlite-core';
import { syncedColumns } from './common';

/**
 * Shape of income_sources.schedule. The precise union (IncomeSchedule) lives in @tahi/core;
 * stored here loosely typed as JSON.
 */
export type IncomeScheduleJson = { kind: string } & Record<string, unknown>;

/**
 * Synced. Money set aside per period (weekly, per cutoff, monthly). Balances are derived from
 * ledger_entries.fund_id; nothing is stored, and a fund starts fresh each period (no carry-over).
 */
export const funds = sqliteTable(
  'funds',
  {
    ...syncedColumns(),
    name: text('name').notNull(),
    period: text('period', { enum: ['weekly', 'per_cutoff', 'monthly'] }).notNull(),
    amountMinor: integer('amount_minor').notNull(),
    currency: text('currency').notNull().default('PHP'),
    startDate: text('start_date').notNull(),
    /** Per-cutoff funds refill on this source's paydays (null = the first salary source). */
    refillFromIncomeSourceId: text('refill_from_income_source_id'),
    position: real('position').notNull().default(0),
    /** v0.5: the money accounts the fund's money is kept in; the first is its default "From". */
    accountIds: text('account_ids', { mode: 'json' })
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'`),
  },
  (t) => [index('funds_user_idx').on(t.userId)],
);

/** Synced. One row per user, id = singletonId(userId, 'budget_settings'). A missing row means the defaults. */
export const budgetSettings = sqliteTable('budget_settings', {
  ...syncedColumns(),
  /** "Deduct spending from my budget automatically" (spec §6), on by default. */
  autoDeduct: integer('auto_deduct', { mode: 'boolean' }).notNull().default(true),
  defaultFundId: text('default_fund_id'),
  /**
   * v0.5 "Use funds (spending envelopes)", off by default (Ivan, 2026-11-03). Off: no fund
   * pickers, tiles or deductions; fund rows are kept and come back when it's turned on.
   */
  useFunds: integer('use_funds', { mode: 'boolean' }).notNull().default(false),
  /** v0.5: the account spending and payments come out of unless the entry or fund picks another. */
  defaultAccountId: text('default_account_id'),
  /** v0.5: the account income lands in unless the income source or entry picks another. */
  incomeAccountId: text('income_account_id'),
});

/** Synced. Salary and bonus sources with their pay schedule and default amounts. */
export const incomeSources = sqliteTable(
  'income_sources',
  {
    ...syncedColumns(),
    name: text('name').notNull(),
    kind: text('kind', { enum: ['salary', 'bonus', 'other'] }).notNull(),
    schedule: text('schedule', { mode: 'json' }).$type<IncomeScheduleJson>().notNull(),
    /** Minor units, one per cutoff (semi-monthly) or per bonus date. */
    defaultAmounts: text('default_amounts', { mode: 'json' })
      .$type<number[]>()
      .notNull()
      .default(sql`'[]'`),
    currency: text('currency').notNull().default('PHP'),
    weekendRule: text('weekend_rule', { enum: ['before', 'after'] })
      .notNull()
      .default('before'),
    /** No paydays are generated before this day. */
    startDate: text('start_date').notNull(),
    /** v0.7.4 "Last payday on or before": no paydays are generated after this day (null = no end). */
    endDate: text('end_date'),
    categoryId: text('category_id'),
    /** v0.5: the account its paydays land in (null = Budget's "Receive into" default). */
    accountId: text('account_id'),
  },
  (t) => [index('income_sources_user_idx').on(t.userId)],
);

/**
 * Synced. Only paydays the user acted on (received or skipped); expected ones are generated from
 * the schedule. id = singletonId(userId, 'paydays.<sourceId>.<scheduledDate>') (conventions §3).
 */
export const paydays = sqliteTable(
  'paydays',
  {
    ...syncedColumns(),
    sourceId: text('source_id').notNull(),
    /** The schedule's own date, before the weekend rule. */
    scheduledDate: text('scheduled_date').notNull(),
    /** The day the money came. */
    actualDate: text('actual_date'),
    defaultAmountMinor: integer('default_amount_minor').notNull(),
    amountMinor: integer('amount_minor'),
    currency: text('currency').notNull().default('PHP'),
    status: text('status', { enum: ['expected', 'received', 'skipped'] })
      .notNull()
      .default('expected'),
    ledgerEntryId: text('ledger_entry_id'),
  },
  (t) => [index('paydays_user_source_idx').on(t.userId, t.sourceId)],
);

/**
 * Synced (v0.5). A real place money sits: Cash, GCash, a bank account. The balance is derived
 * (opening balance + ledger movements dated on or after openingDate), never stored. SQL name
 * money_accounts because `accounts` is Better Auth's table.
 */
export const moneyAccounts = sqliteTable(
  'money_accounts',
  {
    ...syncedColumns(),
    name: text('name').notNull(),
    type: text('type', { enum: ['cash', 'ewallet', 'bank'] }),
    /** What the account held on openingDate ("Balance now"), in centavos; may be negative. */
    openingBalanceMinor: integer('opening_balance_minor').notNull().default(0),
    openingDate: text('opening_date').notNull(),
    currency: text('currency').notNull().default('PHP'),
    position: real('position').notNull().default(0),
    /** v0.6: 'spending' | 'savings' | 'investment' (a GoTyme savings, Pag-IBIG MP2, a time deposit). */
    kind: text('kind', { enum: ['spending', 'savings', 'investment'] })
      .notNull()
      .default('spending'),
    /**
     * v0.6 "Use for spending": on, the account is offered in From chips and counts in Safe to
     * spend; off, it is savings-only (its own Savings section). Independent of the interest rate.
     */
    useForSpending: integer('use_for_spending', { mode: 'boolean' }).notNull().default(true),
    /** v0.6: yearly rate in basis points (300 = 3.00%); null = no interest. */
    annualRateBp: integer('annual_rate_bp'),
    /** v0.6: how interest is credited: 'monthly' | 'yearly' | 'maturity' (paid at maturity). */
    growth: text('growth', { enum: ['monthly', 'yearly', 'maturity'] }),
    /** v0.6: the day interest starts (null = the opening date). */
    startDate: text('start_date'),
    maturityDate: text('maturity_date'),
    /** v0.6: money can't be taken out before maturity (shown as "Locked until"). */
    locked: integer('locked', { mode: 'boolean' }).notNull().default(false),
  },
  (t) => [index('money_accounts_user_idx').on(t.userId)],
);

/**
 * Synced (v0.6). Money owed to you (a friend's share, a loan you gave). Receipts are ledger
 * transfers into an account with sourceId = this row (deposits, never income); what's left to
 * receive is derived from them. Never in Safe to spend until received.
 */
export const receivables = sqliteTable(
  'receivables',
  {
    ...syncedColumns(),
    /** Who owes. */
    name: text('name').notNull(),
    amountMinor: integer('amount_minor').notNull(),
    expectedDate: text('expected_date'),
    note: text('note'),
    currency: text('currency').notNull().default('PHP'),
  },
  (t) => [index('receivables_user_idx').on(t.userId)],
);

/**
 * Synced (v0.5). Money expected to come in that isn't income (vouchers, a leave conversion, a
 * gift): listed under "Coming in", never in income, what's left or safe to spend. Receiving it
 * writes a deposit (a ledger transfer into the account) on id singletonId(u, 'expected.<id>').
 */
export const expectedMoney = sqliteTable(
  'expected_money',
  {
    ...syncedColumns(),
    name: text('name').notNull(),
    amountMinor: integer('amount_minor').notNull(),
    expectedDate: text('expected_date').notNull(),
    /** The account it will land in (null = Budget's "Receive into" default). */
    accountId: text('account_id'),
    status: text('status', { enum: ['expected', 'received'] })
      .notNull()
      .default('expected'),
    receivedDate: text('received_date'),
    receivedMinor: integer('received_minor'),
    ledgerEntryId: text('ledger_entry_id'),
    currency: text('currency').notNull().default('PHP'),
    /**
     * v0.7.4: 'none' (a one-off: the status fields above), or 'monthly' / 'yearly' from
     * expectedDate. A repeating item's occurrences are generated; the ones acted on are
     * expected_receipts rows (its own status fields stay 'expected').
     */
    repeat: text('repeat', { enum: ['none', 'monthly', 'yearly'] })
      .notNull()
      .default('none'),
    /** v0.7.4: the last day an occurrence may fall on (null = no end). */
    repeatUntil: text('repeat_until'),
  },
  (t) => [index('expected_money_user_idx').on(t.userId)],
);

/**
 * Synced (v0.7.4). One occurrence of a repeating Coming in item that was received or skipped
 * (like paydays: expected ones are generated, never stored). id = singletonId(u,
 * 'expected_receipts.<expectedId>.<occurrenceDate>'); undo is a soft delete. A receipt with an
 * account has a deposit on singletonId(u, 'expected.<expectedId>.<occurrenceDate>').
 */
export const expectedReceipts = sqliteTable(
  'expected_receipts',
  {
    ...syncedColumns(),
    expectedId: text('expected_id').notNull(),
    /** The occurrence's own scheduled day (the key). */
    occurrenceDate: text('occurrence_date').notNull(),
    status: text('status', { enum: ['received', 'skipped'] })
      .notNull()
      .default('received'),
    receivedDate: text('received_date'),
    amountMinor: integer('amount_minor'),
    accountId: text('account_id'),
    ledgerEntryId: text('ledger_entry_id'),
    currency: text('currency').notNull().default('PHP'),
  },
  (t) => [index('expected_receipts_user_expected_idx').on(t.userId, t.expectedId)],
);
